import React, { useEffect, useRef, useCallback, useState } from 'react';

/**
 * Generic Canvas Component
 *
 * A reusable canvas component that can be configured with different drawing functions.
 * Handles canvas setup, sizing, and provides a clean API for custom drawing logic.
 *
 * @param {Object} props - Component props
 * @param {Function} props.drawFunction - Function that handles all drawing logic
 * @param {Array} props.drawDependencies - Array of dependencies that trigger redraw
 * @param {Object} props.canvasStyle - Optional custom styles for the canvas
 * @param {Object} props.containerStyle - Optional custom styles for the container
 * @param {Function} props.onMouseMove - Function to handle mouse move events
 * @param {Function} props.onMouseLeave - Function to handle mouse leave events
 * @param {Function} props.onClick - Function to handle click events
 */
const Canvas = ({
    drawFunction,
    drawDependencies = [],
    canvasStyle = {},
    containerStyle = {},
    onMouseMove,
    onMouseLeave,
    onClick,
}) => {
    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const [canvasSize, setCanvasSize] = useState({ width: 800, height: 600 });

    // Update canvas size based on container
    const updateCanvasSize = useCallback(() => {
        if (containerRef.current) {
            const { clientWidth, clientHeight } = containerRef.current;
            setCanvasSize({ width: clientWidth, height: clientHeight });
        }
    }, []);

    /**
     * Execute the provided drawing function
     */
    const draw = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas || !drawFunction) return;

        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Call the provided drawing function with canvas context and size
        drawFunction(ctx, canvasSize);
    }, [drawFunction, canvasSize, ...drawDependencies]);

    // Set up canvas and draw when component mounts or container size changes
    useEffect(() => {
        updateCanvasSize();

        // Use ResizeObserver to watch for container size changes
        const resizeObserver = new ResizeObserver(() => {
            updateCanvasSize();
        });

        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }

        return () => {
            resizeObserver.disconnect();
        };
    }, [updateCanvasSize]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (canvas) {
            canvas.width = canvasSize.width;
            canvas.height = canvasSize.height;
            draw();
        }
    }, [canvasSize, draw]);

    const DEFAULT_CONTAINER_STYLE = {
        width: '100%',
        height: '100%',
        minHeight: '400px',
        ...containerStyle,
    };

    const DEFAULT_CANVAS_STYLE = {
        border: '1px solid #dee2e6',
        borderRadius: '4px',
        backgroundColor: '#ffffff',
        width: '100%',
        height: '100%',
        display: 'block',
        ...canvasStyle,
    };

    return (
        <div
            ref={containerRef}
            className="canvas-container"
            style={DEFAULT_CONTAINER_STYLE}
        >
            <canvas
                ref={canvasRef}
                style={DEFAULT_CANVAS_STYLE}
                onMouseMove={onMouseMove}
                onMouseLeave={onMouseLeave}
                onClick={onClick}
            />
        </div>
    );
};

export default Canvas; 